import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme, APP_THEMES } from '../contexts/ThemeContext';
import { useReadingProgress } from '../contexts/ReadingProgressContext';
import { usePro, PRO_FEATURES } from '../contexts/ProContext';
import { Globe, Palette, Layers, ChevronDown, Menu, Home, BookOpen, Search, ZoomIn, Clock, BookText, Scan, HandHeart, Compass, MapPin, TrendingUp, Bookmark, Settings, Lock, Crown, Radio, Moon, Sun, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { languages } from '../i18n';
import { toast } from 'sonner';
import ClassicPrayerRow from './ClassicPrayerRow';

// Helper function to get Hijri date
const getHijriDate = (lang = 'en') => {
  try {
    const formatter = new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA-u-ca-islamic' : 'en-u-ca-islamic', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    return formatter.format(new Date());
  } catch (e) {
    return '';
  }
};

// Helper function to get Gregorian date
const getGregorianDate = (lang = 'en') => {
  try {
    const formatter = new Intl.DateTimeFormat(lang, {
      day: 'numeric',
      month: 'short',
    });
    return formatter.format(new Date());
  } catch (e) {
    return '';
  }
};

// Juz to Surah mapping
const JUZ_MAPPING = [
  { juz: 1, startSurah: 1, startAyah: 1 },
  { juz: 2, startSurah: 2, startAyah: 142 },
  { juz: 3, startSurah: 2, startAyah: 253 },
  { juz: 4, startSurah: 3, startAyah: 93 },
  { juz: 5, startSurah: 4, startAyah: 24 },
  { juz: 6, startSurah: 4, startAyah: 148 },
  { juz: 7, startSurah: 5, startAyah: 83 },
  { juz: 8, startSurah: 6, startAyah: 111 },
  { juz: 9, startSurah: 7, startAyah: 88 },
  { juz: 10, startSurah: 8, startAyah: 41 },
  { juz: 11, startSurah: 9, startAyah: 93 },
  { juz: 12, startSurah: 11, startAyah: 6 },
  { juz: 13, startSurah: 12, startAyah: 53 },
  { juz: 14, startSurah: 15, startAyah: 1 },
  { juz: 15, startSurah: 17, startAyah: 1 },
  { juz: 16, startSurah: 18, startAyah: 75 },
  { juz: 17, startSurah: 21, startAyah: 1 },
  { juz: 18, startSurah: 23, startAyah: 1 },
  { juz: 19, startSurah: 25, startAyah: 21 },
  { juz: 20, startSurah: 27, startAyah: 56 },
  { juz: 21, startSurah: 29, startAyah: 46 },
  { juz: 22, startSurah: 33, startAyah: 31 },
  { juz: 23, startSurah: 36, startAyah: 28 },
  { juz: 24, startSurah: 39, startAyah: 32 },
  { juz: 25, startSurah: 41, startAyah: 47 },
  { juz: 26, startSurah: 46, startAyah: 1 },
  { juz: 27, startSurah: 51, startAyah: 31 },
  { juz: 28, startSurah: 58, startAyah: 1 },
  { juz: 29, startSurah: 67, startAyah: 1 },
  { juz: 30, startSurah: 78, startAyah: 1 },
];

// Theme color configurations - match homepage header colors for each theme
// Kids & Classic: Light/white Row 1 to match homepage
// Emerald & Royal: Dark colored rows
// "Beyond Elegance" is WHITE for Emerald, Royal, and dark mode themes
const getThemeColors = (appTheme, mode) => {
  switch (appTheme) {
    case 'emerald':
      return {
        // Match the premium Emerald homepage surface system.
        row1Bg: '#0A3022',
        row2Bg: '#0B3B2E',
        textPrimary: 'text-white',
        textSecondary: 'text-[#CBB373]',
        taglineColor: '#ffffff', // WHITE for Emerald
        menuBtnBg: 'bg-[#124332] border-[#CBB373]/30',
        menuBtnIcon: 'text-white',
        juzBtnBg: 'bg-[#CBB373]',
        juzBtnText: 'text-[#0A3022]',
        iconBtnBorder: 'border-[#CBB373]/50',
        iconBtnText: 'text-[#CBB373]',
        iconBtnHover: 'hover:bg-[#CBB373]/20',
        progressBg: 'bg-[#0A3022]',
        progressIndicator: '!bg-[#CBB373]',
        progressText: 'text-white',
        // Page title styling
        titleBg: '#0B3B2E',
        titleText: 'text-white',
      };
    case 'royal':
      return {
        // Row 1: Darker navy
        row1Bg: '#141c32',
        // Row 2: Slightly lighter navy/purple
        row2Bg: '#1e2a4a',
        textPrimary: 'text-white',
        textSecondary: 'text-[#c9a227]',
        taglineColor: '#ffffff', // WHITE for Royal Night
        menuBtnBg: 'bg-[#1a2540] border-[#c9a227]/30',
        menuBtnIcon: 'text-white',
        juzBtnBg: 'bg-[#c9a227]',
        juzBtnText: 'text-[#1e2a4a]',
        iconBtnBorder: 'border-[#c9a227]/50',
        iconBtnText: 'text-[#c9a227]',
        iconBtnHover: 'hover:bg-[#c9a227]/20',
        progressBg: 'bg-[#141c32]',
        progressIndicator: '!bg-[#c9a227]',
        progressText: 'text-white',
        // Page title styling
        titleBg: '#1a2540',
        titleText: 'text-white',
      };
    case 'kids':
      return {
        // Row 1: WHITE to match homepage (like Classic)
        row1Bg: '#ffffff',
        // Row 2: Pink (the colorful part)
        row2Bg: '#ec4899',
        textPrimary: 'text-[#831843]',
        textSecondary: 'text-[#db2777]',
        taglineColor: '#db2777',
        menuBtnBg: 'bg-[#ec4899] border-white/30',
        menuBtnIcon: 'text-white',
        juzBtnBg: 'bg-[#06b6d4]',
        juzBtnText: 'text-white',
        iconBtnBorder: 'border-white/50',
        iconBtnText: 'text-white',
        iconBtnHover: 'hover:bg-white/20',
        progressBg: 'bg-[#fce7f3]',
        progressIndicator: '!bg-[#ec4899]',
        progressText: 'text-[#831843]',
        // Page title styling
        titleBg: '#fdf2f8',
        titleText: 'text-[#831843]',
        // Use dark logo on white Row 1
        useDarkLogo: true,
      };
    case 'classic':
    default:
      // For Classic in dark mode (night vision), use white tagline
      const isNightVision = mode === 'dark';
      return {
        // Row 1: CREAM to match homepage
        row1Bg: isNightVision ? '#1e293b' : '#F1EBDD',
        // Row 2: (hidden for classic - replaced with ClassicPrayerRow)
        row2Bg: '#F1EBDD',
        textPrimary: isNightVision ? 'text-white' : 'text-[#0F4C3A]',
        textSecondary: 'text-[#C9A227]',
        taglineColor: isNightVision ? '#ffffff' : '#C9A227',
        menuBtnBg: 'bg-[#0F4C3A] border-[#C9A227]/40',
        menuBtnIcon: 'text-[#C9A227]',
        juzBtnBg: 'bg-[#C9A227]',
        juzBtnText: 'text-[#0F4C3A]',
        iconBtnBorder: 'border-[#C9A227]/50',
        iconBtnText: 'text-[#0F4C3A]',
        iconBtnHover: 'hover:bg-[#C9A227]/20',
        progressBg: isNightVision ? 'bg-[#1e293b]' : 'bg-[#F1EBDD]',
        progressIndicator: '!bg-[#0F4C3A]',
        progressText: isNightVision ? 'text-white' : 'text-[#0F4C3A]',
        // Page title styling
        titleBg: isNightVision ? '#1e293b' : '#F1EBDD',
        titleText: isNightVision ? 'text-white' : 'text-[#0F4C3A]',
        // Use dark logo on cream Row 1 (only in light mode)
        useDarkLogo: !isNightVision,
      };
  }
};

export const PageHeader = ({ 
  showJuzButton = false, 
  pageTitle = '', 
  showProgressBar = true,
  extractedDate = null,
  hasExtractionData = false
}) => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const { appTheme, setAppTheme, mode, theme, toggleTheme } = useTheme();
  const { getCompletionPercentage } = useReadingProgress();
  const { isPro, requestFeatureAccess, isFeatureUnlockedViaAd } = usePro();
  const navigate = useNavigate();
  
  const [juzDialogOpen, setJuzDialogOpen] = React.useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = React.useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  const completionPercentage = getCompletionPercentage();
  const colors = getThemeColors(appTheme, mode);
  
  // Show hamburger menu when:
  //  - user selected 'hamburger' style, OR
  //  - user selected 'fab' / 'bottom' but doesn't have Pro/ad access to it
  //    (safety fallback so the user is never left without navigation)
  const showHamburgerMenu = (() => {
    const style = settings.floatingMenuStyle;
    if (!style || style === 'hamburger') return true;
    const hasNavAccess = isPro || isFeatureUnlockedViaAd('bottom_nav');
    return !hasNavAccess;
  })();
  
  // Pro-gated Night Vision toggle - STRICT PRO ONLY (NO AD UNLOCK)
  const handleNightVisionToggle = () => {
    // If currently in light mode, switching to dark requires Pro (NO ad unlock)
    if (mode === 'light') {
      if (!isPro) {
        // Night Vision is PRO ONLY - show upgrade prompt, no ad option
        requestFeatureAccess({ 
          ...PRO_FEATURES.NIGHT_VISION,
          noAdUnlock: true // Signal that ad unlock is not available
        });
        return;
      }
    }
    // If in dark mode, can always switch back to light (free)
    toggleTheme();
  };
  
  // Night Vision icon visible only for Pro users or if already in dark mode
  // (users already in dark mode can switch back to light for free)
  const showNightVisionIcon = isPro || mode === 'dark';
  
  // Date display logic:
  // TECHNICAL REQUIREMENT: Date header must be 100% data-bound to extracted prayer time data
  // - It ONLY shows the date from the extracted prayer time payload
  // - If no extracted date exists, NO date is shown (prevents showing wrong/stale dates)
  // - This makes the date header a "source of truth" validator - if it shows yesterday's date,
  //   the user knows the data is stale without needing extra programming logic
  const getDisplayDate = () => {
    // ONLY show date if it comes from the extracted prayer time data
    if (extractedDate) {
      return extractedDate; // Direct binding to extracted data
    }
    // No extracted date = no date display (data is either missing or stale)
    return null;
  };
  
  const displayDate = getDisplayDate();

  const openMushafMode = () => {
    const surahToOpen = settings.lastReadSurah || 1;
    navigate(`/quran/mushaf/${surahToOpen}`);
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setLanguageDialogOpen(false);
    const lang = languages.find(l => l.code === langCode);
    toast.success(`Language: ${lang?.nativeName || langCode}`);
  };

  const handleThemeChange = (themeId) => {
    setAppTheme(themeId);
    setThemeDialogOpen(false);
    toast.success(`Theme changed`);
  };

  const renderJuzDialog = () => (
    <DialogContent className="sm:max-w-md max-h-[80vh]">
      <DialogHeader>
        <DialogTitle className="font-serif text-xl flex items-center gap-2">
          <Layers className="h-5 w-5" />
          {t('app.selectJuz', 'Select Juz')}
        </DialogTitle>
      </DialogHeader>
      <ScrollArea className="h-[50vh] pr-4">
        <div className="grid grid-cols-5 gap-2 py-2">
          {JUZ_MAPPING.map((juz) => (
            <Link
              key={juz.juz}
              to={`/quran/${juz.startSurah}`}
              onClick={() => setJuzDialogOpen(false)}
              className="aspect-square flex items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary/20 hover:from-primary hover:to-primary/80 hover:text-white transition-all font-bold text-lg border border-primary/20 hover:border-primary"
            >
              {juz.juz}
            </Link>
          ))}
        </div>
      </ScrollArea>
    </DialogContent>
  );

  const renderLanguageDialog = () => (
    <DialogContent className="sm:max-w-md max-h-[80vh]">
      <DialogHeader>
        <DialogTitle className="font-serif text-xl flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Select Language
        </DialogTitle>
      </DialogHeader>
      <ScrollArea className="h-[50vh] pr-4">
        <div className="space-y-1 py-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                i18n.language === lang.code
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-foreground'
              }`}
            >
              <span className="text-2xl">{lang.flag}</span>
              <div>
                <span className="font-medium block">{lang.nativeName}</span>
                <span className={`text-xs ${i18n.language === lang.code ? 'text-primary-foreground/80' : 'text-foreground/60'}`}>{lang.name}</span>
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>
    </DialogContent>
  );

  const renderThemeDialog = () => (
    <DialogContent className="sm:max-w-md" data-theme-modal="true">
      <DialogHeader>
        <DialogTitle className="font-serif text-xl">{t('common.chooseTheme', "Choose Theme")}</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-4">
        {APP_THEMES && Object.values(APP_THEMES).map((themeOption) => {
          const isSelected = appTheme === themeOption.id;
          const isFreeTheme = themeOption.id === 'classic';
          // Theme Selection is STRICT PRO ONLY (NO AD UNLOCK)
          const hasAccess = isPro;
          const isLocked = !hasAccess && !isFreeTheme;
          
          return (
            <button
              key={themeOption.id}
              onClick={() => {
                if (isLocked) {
                  // Themes are PRO ONLY - no ad unlock option
                  requestFeatureAccess({ 
                    id: 'all_themes', 
                    name: 'Premium Themes',
                    noAdUnlock: true // Signal that ad unlock is not available
                  }, () => {
                    handleThemeChange(themeOption.id);
                  });
                } else {
                  handleThemeChange(themeOption.id);
                }
              }}
              data-testid={`theme-option-${themeOption.id}`}
              className={`relative text-left rounded-xl border-2 overflow-hidden transition-all ${
                isSelected
                  ? 'border-primary shadow-lg ring-2 ring-primary/20'
                  : isLocked 
                    ? 'border-border opacity-70'
                    : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="h-12 flex">
                <div className="flex-1" style={{ backgroundColor: themeOption.preview?.background || '#1e2a4a' }} />
                <div className="w-4" style={{ backgroundColor: themeOption.preview?.primary || '#c9a227' }} />
                <div className="w-4" style={{ backgroundColor: themeOption.preview?.secondary || '#8B5CF6' }} />
              </div>
              <div className="p-2 bg-[#f5f0e6]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-black">{themeOption.name}</p>
                    <p className="text-[10px] text-gray-700">{themeOption.description}</p>
                  </div>
                  {isLocked && (
                    <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] font-bold flex items-center gap-0.5">
                      PRO
                    </span>
                  )}
                  {isFreeTheme && !hasAccess && (
                    <span className="px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-700 text-[9px] font-medium">
                      FREE
                    </span>
                  )}
                </div>
              </div>
              {isSelected && (
                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </DialogContent>
  );

  // Navigation items for mobile menu
  const navItems = [
    { path: '/', label: t('nav.home', 'Home'), icon: Home },
    { path: '/quran', label: t('nav.quran', 'Quran'), icon: BookOpen },
    { path: '/prayer-times', label: t('nav.prayerTimes', 'Prayer Times'), icon: Clock },
    { path: '/duas', label: t('nav.duas', 'Duas'), icon: HandHeart },
    { path: '/hadith', label: t('nav.hadith', 'Hadith'), icon: BookText },
    { path: '/mosques', label: t('nav.masjidGuide', 'Masjid Guide'), icon: MapPin, isPro: true, featureId: 'mosque_finder' },
    { path: '/prayer-times#masjid-connect', label: t('nav.masjidConnect', 'Masjid Connect'), icon: Radio, isPro: true, featureId: 'masjid_connect' },
    { path: '/halal-scanner', label: t('nav.halalScanner', 'Halal Scanner'), icon: Scan, isPro: true, featureId: 'halal_scanner' },
    { path: '/progress', label: t('nav.progress', 'Progress'), icon: TrendingUp, isPro: true, featureId: 'reading_stats' },
    { path: '/quran/zoom/1', label: t('nav.zoom', 'Zoom'), icon: ZoomIn, isPro: true, featureId: 'zoom_mode' },
    { path: '/qibla', label: t('nav.qibla', 'Qibla'), icon: Compass },
    { path: '/bookmarks', label: t('nav.saved', 'Bookmarks'), icon: Bookmark },
    // Search hidden as per user request
    { path: '/settings', label: t('nav.settings', 'Settings'), icon: Settings },
  ];

  return (
    <div className="relative overflow-hidden">
      {/* Row 1: Logo + Tanzeen + Beyond Elegance | Dates | Hamburger Menu */}
      <div 
        className="flex items-center justify-between px-4 pt-3 pb-2"
        style={{ backgroundColor: colors.row1Bg }}
      >
        <Link to="/" className="flex items-center gap-3">
          <img 
            src={colors.useDarkLogo ? "/images/tanzeel-logo-black.png" : "/images/tanzeel-logo.png"} 
            alt="T+" 
            className="h-10 w-10 object-contain"
          />
          <div>
            <span className={`font-serif text-xl font-bold ${colors.textPrimary}`}>
              Tanzeen <span className="font-black" style={{fontWeight: 900}}>+</span>
            </span>
            <p className="text-[9px] italic -mt-0.5 beyond-elegance-text" style={{ color: colors.taglineColor }}>
              {t('app.tagline', 'Beyond Elegance')}
            </p>
          </div>
        </Link>
        
        {/* Date Display moved to PrayerTrialPage (discrete, under page title) */}
        
        {/* Right side: Night Vision toggle + Hamburger menu */}
        <div className="flex items-center gap-2">
          {/* Language + Theme icons (Classic theme only, matches homepage header) */}
          {appTheme === 'classic' && (
            <>
              <Dialog open={languageDialogOpen} onOpenChange={setLanguageDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    className="p-2 rounded-xl transition-colors text-[#0F4C3A] hover:bg-[#C9A227]/15 h-12 w-12 flex items-center justify-center"
                    data-testid="header-language-btn"
                  >
                    <Globe className="h-5 w-5" />
                  </button>
                </DialogTrigger>
                {renderLanguageDialog()}
              </Dialog>
              <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    className="p-2 rounded-xl transition-colors text-[#0F4C3A] hover:bg-[#C9A227]/15 h-12 w-12 flex items-center justify-center"
                    data-testid="header-theme-btn"
                  >
                    <Palette className="h-5 w-5" />
                  </button>
                </DialogTrigger>
                {renderThemeDialog()}
              </Dialog>
            </>
          )}

          {/* Night Vision Toggle - Only visible for Pro users or if in dark mode */}
          {showNightVisionIcon && (
            <button 
              onClick={handleNightVisionToggle}
              className="p-2 rounded-lg transition-colors"
              style={{ color: colors.taglineColor }}
              data-testid="header-night-vision-toggle"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          )}
          
          {/* Hamburger Menu Button - Only show if user selected hamburger style */}
          {showHamburgerMenu && (
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button 
                className={`p-3 rounded-xl border ${colors.menuBtnBg}`}
                data-testid="header-menu-btn"
              >
                <Menu className={`h-6 w-6 ${colors.menuBtnIcon}`} />
              </button>
            </SheetTrigger>
            {/* Royal Knight Theme - Special elegant menu */}
            {appTheme === 'royal' ? (
            <SheetContent side="right" className="w-80 p-0 border-0 !bg-transparent [&>button]:text-white [&>button]:opacity-100" style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)' }}>
              <div className="flex h-full flex-col">
                {/* Royal Knight Header */}
                <div className="p-6" style={{ background: 'linear-gradient(135deg, #2a2a4a 0%, #1e1e3a 100%)', borderBottom: '2px solid rgba(201, 162, 39, 0.4)' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img src="/images/tanzeel-logo.png" alt="Tanzeen +" className="h-10 w-10 object-contain" />
                      <div>
                        <div style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: 700, fontFamily: 'serif', letterSpacing: '0.025em', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                          Tanzeen <span style={{ color: '#c9a227', fontWeight: 900 }}>+</span>
                        </div>
                        <div style={{ color: '#ffffff', fontSize: '10px', fontStyle: 'italic', letterSpacing: '0.05em', opacity: 0.9 }}>{t('app.tagline')}</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Royal Knight Navigation */}
                <ScrollArea className="flex-1 py-4">
                  <nav className="space-y-2 px-4">
                    {navItems.map((item) => {
                      const Icon = item.icon;
                      const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                      const isActive = window.location.pathname === item.path || 
                                      (item.path === '/quran' && window.location.pathname.startsWith('/quran'));
                      
                      // Royal Knight cream card styling
                      const navItemStyle = isActive 
                        ? {
                            background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #7c3aed 100%)',
                            boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                            border: '1px solid rgba(139, 92, 246, 0.5)',
                          }
                        : {
                            background: 'linear-gradient(135deg, #f5f0e6 0%, #ebe5d9 100%)',
                            border: '1px solid rgba(201, 162, 39, 0.4)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          };
                      
                      const iconContainerStyle = isActive 
                        ? {
                            background: 'rgba(255, 255, 255, 0.2)',
                            border: '2px solid rgba(255, 255, 255, 0.4)',
                          }
                        : {
                            background: 'rgba(201, 162, 39, 0.2)',
                            border: '2px solid #b8860b',
                          };
                      
                      const textColor = isActive ? '#ffffff' : '#2d2a26';
                      const iconColor = isActive ? '#ffffff' : '#10b981';
                      
                      const handleNavClick = (e) => {
                        if (isItemLocked) {
                          e.preventDefault();
                          setMobileMenuOpen(false);
                          requestFeatureAccess(
                            { id: item.featureId, name: item.label },
                            () => { window.location.href = item.path; }
                          );
                          return;
                        }
                        setMobileMenuOpen(false);
                      };
                      
                      return (
                        <Link
                          key={item.path}
                          to={isItemLocked ? '#' : item.path}
                          onClick={handleNavClick}
                          className="flex items-center gap-4 rounded-2xl px-4 py-4 transition-all duration-300 no-underline"
                          style={{...navItemStyle, textDecoration: 'none'}}
                        >
                          <div className="rounded-xl p-2.5 flex-shrink-0" style={iconContainerStyle}>
                            {Icon && <Icon className="h-5 w-5" style={{ color: iconColor, stroke: iconColor }} strokeWidth={2} />}
                          </div>
                          <span 
                            className="flex-1 font-semibold text-base tracking-wide"
                            style={{ color: textColor, WebkitTextFillColor: textColor, textShadow: isActive ? '0 1px 2px rgba(0,0,0,0.3)' : 'none' }}
                          >
                            {item.label}
                          </span>
                          {isItemLocked && (
                            <span 
                              className="px-2 py-1 rounded-lg text-[9px] font-bold flex items-center gap-1 flex-shrink-0"
                              style={{ background: 'linear-gradient(135deg, #c9a227 0%, #d4af37 100%)', color: '#0f0f1a', boxShadow: '0 2px 8px rgba(201, 162, 39, 0.3)' }}
                            >
                              <Crown className="h-2.5 w-2.5" />
                              PRO
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </nav>
                </ScrollArea>
              </div>
            </SheetContent>
            ) : appTheme === 'kids' ? (
            /* Kids Paradise - Magical dreamy menu */
            <SheetContent 
              side="right" 
              className="w-80 p-0 border-0 overflow-hidden"
              style={{ background: 'linear-gradient(180deg, #fce7f3 0%, #e9d5ff 30%, #dbeafe 60%, #cffafe 100%)' }}
            >
              <div className="flex h-full flex-col relative">
                {/* Floating Stars/Sparkles decoration */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-8 left-4 text-2xl animate-pulse">✨</div>
                  <div className="absolute top-20 right-8 text-xl animate-bounce" style={{ animationDelay: '0.5s' }}>⭐</div>
                  <div className="absolute top-40 left-8 text-lg animate-pulse" style={{ animationDelay: '1s' }}>🌟</div>
                  <div className="absolute bottom-32 right-4 text-2xl animate-bounce" style={{ animationDelay: '0.3s' }}>✨</div>
                  <div className="absolute bottom-20 left-6 text-xl animate-pulse" style={{ animationDelay: '0.7s' }}>⭐</div>
                </div>
                
                {/* Magical Rainbow Header */}
                <div 
                  className="p-6 relative"
                  style={{ background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #06b6d4 100%)' }}
                >
                  <div className="flex items-center justify-between relative z-10">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <img src="/images/tanzeel-logo.png" alt="Tanzeen +" className="h-10 w-10 object-contain" />
                        <span className="absolute -top-1 -right-1 text-sm">✨</span>
                      </div>
                      <div>
                        <span className="font-serif text-xl font-bold text-white drop-shadow-lg">
                          Tanzeen <span className="font-black" style={{fontWeight: 900}}>+</span>
                        </span>
                        <p className="text-xs text-white/90 italic">{t('app.tagline', 'Beyond Elegance')}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setMobileMenuOpen(false)}
                      className="rounded-full h-10 w-10 text-white hover:bg-white/20 backdrop-blur-sm"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                
                {/* Magical Navigation */}
                <ScrollArea className="flex-1 py-4 relative z-10">
                  <nav className="space-y-2 px-4">
                    {navItems.map((item, index) => {
                      const Icon = item.icon;
                      const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                      const isActive = window.location.pathname === item.path || 
                                      (item.path === '/quran' && window.location.pathname.startsWith('/quran'));
                      
                      // Magical rainbow colors for each nav item
                      const magicColors = [
                        { bg: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', shadow: 'rgba(236, 72, 153, 0.4)' },
                        { bg: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)', shadow: 'rgba(168, 85, 247, 0.4)' },
                        { bg: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)', shadow: 'rgba(6, 182, 212, 0.4)' },
                        { bg: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', shadow: 'rgba(245, 158, 11, 0.4)' },
                        { bg: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', shadow: 'rgba(16, 185, 129, 0.4)' },
                        { bg: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', shadow: 'rgba(59, 130, 246, 0.4)' },
                        { bg: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', shadow: 'rgba(239, 68, 68, 0.4)' },
                        { bg: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', shadow: 'rgba(139, 92, 246, 0.4)' },
                      ];
                      const colorScheme = magicColors[index % magicColors.length];
                      
                      const handleNavClick = (e) => {
                        if (isItemLocked) {
                          e.preventDefault();
                          setMobileMenuOpen(false);
                          requestFeatureAccess(
                            { id: item.featureId, name: item.label },
                            () => { window.location.href = item.path; }
                          );
                          return;
                        }
                        setMobileMenuOpen(false);
                      };
                      
                      return (
                        <Link
                          key={item.path}
                          to={isItemLocked ? '#' : item.path}
                          onClick={handleNavClick}
                          className={`flex items-center gap-4 rounded-2xl px-4 py-3.5 transition-all duration-300 ${
                            isActive ? '' : 'hover:scale-[1.02]'
                          }`}
                          style={{
                            background: isActive 
                              ? 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #06b6d4 100%)'
                              : 'rgba(255, 255, 255, 0.7)',
                            boxShadow: isActive 
                              ? '0 4px 20px rgba(168, 85, 247, 0.4), 0 0 30px rgba(236, 72, 153, 0.2)'
                              : '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: isActive 
                              ? '2px solid rgba(255, 255, 255, 0.3)'
                              : '2px solid rgba(168, 85, 247, 0.2)',
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <div 
                            className="rounded-xl p-2.5 flex-shrink-0"
                            style={{
                              background: isActive ? 'rgba(255, 255, 255, 0.3)' : colorScheme.bg,
                              boxShadow: isActive ? 'none' : `0 4px 12px ${colorScheme.shadow}`,
                            }}
                          >
                            {Icon && <Icon className="h-5 w-5 text-white" strokeWidth={2} />}
                          </div>
                          <span 
                            className="flex-1 font-bold text-base"
                            style={{ color: isActive ? '#ffffff' : '#581c87' }}
                          >
                            {item.label}
                          </span>
                          {isItemLocked && (
                            <span 
                              className="px-2 py-1 rounded-full text-[9px] font-bold flex items-center gap-1"
                              style={{ 
                                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', 
                                color: '#7c2d12',
                                boxShadow: '0 2px 8px rgba(251, 191, 36, 0.4)'
                              }}
                            >
                              <Crown className="h-2.5 w-2.5" />
                              PRO
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </nav>
                </ScrollArea>
                
                {/* Magical Footer */}
                <div className="p-4 border-t border-purple-200/50 relative z-10">
                  <div 
                    className="rounded-2xl p-4 text-center"
                    style={{ background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)' }}
                  >
                    <p className="text-sm font-medium text-purple-600">
                      ✨ Tanzeen + ✨
                    </p>
                    <p className="text-xs text-purple-400 mt-0.5">{t('app.tagline', 'Beyond Elegance')}</p>
                  </div>
                </div>
              </div>
            </SheetContent>
            ) : (
            /* Other themes - Default menu with colorful icons */
            <SheetContent 
              side="right" 
              className="w-80 p-0 border-0" 
              style={{ backgroundColor: appTheme === 'emerald' ? (mode === 'dark' ? '#031B12' : '#0d4a35') : appTheme === 'classic' ? (mode === 'dark' ? '#0B1220' : '#F1EBDD') : undefined }}
            >
              <div className="flex h-full flex-col">
                {/* Menu Header - Emerald: gold header, Classic: dark green header, Kids: white with border */}
                <div 
                  className={`p-6 ${appTheme === 'emerald' || appTheme === 'classic' ? '' : 'border-b border-border'}`}
                  style={{ 
                    backgroundColor: appTheme === 'emerald' ? undefined : appTheme === 'classic' ? '#0F4C3A' : colors.row1Bg,
                    background: appTheme === 'emerald' ? 'linear-gradient(135deg, #c9a227 0%, #d4b030 100%)' : undefined
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img 
                        src={((appTheme === 'classic' || appTheme === 'kids') && mode === 'light') 
                          ? "/images/tanzeel-logo-black.png" 
                          : "/images/tanzeel-logo.png"
                        }
                        alt="Tanzeen +" 
                        className="h-8 w-8 object-contain"
                      />
                      <div>
                        <span className={`font-serif text-xl font-bold block ${appTheme === 'emerald' ? 'text-black' : appTheme === 'classic' ? 'text-[#F1EBDD]' : 'text-foreground'}`}>
                          Tanzeen <span className="font-black" style={{fontWeight: 900, WebkitTextStroke: "1px currentColor"}}>+</span>
                        </span>
                        <p className={`text-xs italic ${appTheme === 'emerald' ? 'text-black/70' : appTheme === 'classic' ? 'text-[#C9A227]' : 'text-muted-foreground'}`}>{t('app.tagline', 'Beyond Elegance')}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`rounded-xl ${appTheme === 'emerald' ? 'hover:bg-black/10 text-black' : appTheme === 'classic' ? 'hover:bg-white/10 text-[#F1EBDD]' : 'hover:bg-muted text-foreground'} h-10 w-10`}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                
                {/* Navigation with colorful icons */}
                <nav className="flex-1 space-y-1 p-3 overflow-y-auto" style={{ backgroundColor: appTheme === 'emerald' ? colors.row2Bg : undefined }}>
                  {navItems.map((item, index) => {
                    const Icon = item.icon;
                    const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                    const isActive = window.location.pathname === item.path || 
                                    (item.path === '/quran' && window.location.pathname.startsWith('/quran'));
                    
                    // Colorful gradient icons for Classic/Kids themes
                    const iconColors = [
                      'from-blue-500 to-blue-600',
                      'from-amber-500 to-orange-500',
                      'from-emerald-500 to-teal-500',
                      'from-pink-500 to-rose-500',
                      'from-purple-500 to-indigo-500',
                      'from-cyan-500 to-sky-500',
                      'from-yellow-500 to-amber-500',
                      'from-slate-500 to-gray-600',
                    ];
                    const iconGradient = iconColors[index % iconColors.length];
                    
                    // Emerald theme uses gold icons
                    const isEmeraldTheme = appTheme === 'emerald';
                    const isClassicTheme = appTheme === 'classic';
                    
                    const handleNavClick = (e) => {
                      if (isItemLocked) {
                        e.preventDefault();
                        setMobileMenuOpen(false);
                        requestFeatureAccess(
                          { id: item.featureId, name: item.label },
                          () => { window.location.href = item.path; }
                        );
                        return;
                      }
                      setMobileMenuOpen(false);
                    };
                    
                    return (
                      <Link
                        key={item.path}
                        to={isItemLocked ? '#' : item.path}
                        onClick={handleNavClick}
                        className={`flex items-center gap-4 rounded-2xl px-4 py-4 transition-all ${
                          isActive
                            ? isEmeraldTheme 
                              ? 'bg-gradient-to-r from-[#c9a227] to-[#d4b030] shadow-lg'
                              : isClassicTheme
                                ? 'bg-[#0F4C3A] text-[#C9A227] shadow-md'
                                : 'bg-primary text-primary-foreground shadow-lg'
                            : isEmeraldTheme
                              ? 'text-white hover:bg-white/10'
                              : isClassicTheme
                                ? 'text-[#0F4C3A] hover:bg-[#C9A227]/15'
                                : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className={`rounded-xl p-2.5 ${
                          isActive 
                            ? (isEmeraldTheme ? 'bg-black/10' : isClassicTheme ? 'bg-[#C9A227]/25' : 'bg-white/20')
                            : isEmeraldTheme 
                              ? 'bg-[#c9a227]/20'
                              : isClassicTheme
                                ? 'bg-[#C9A227]/20 border border-[#C9A227]/40'
                                : `bg-gradient-to-br ${iconGradient} shadow-md`
                        }`}>
                          {Icon && <Icon 
                            className={`h-5 w-5 ${!isEmeraldTheme && !isClassicTheme && !isActive ? 'text-white' : ''}`} 
                            style={{ 
                              color: isEmeraldTheme && !isActive 
                                ? '#c9a227' 
                                : isClassicTheme
                                  ? (isActive ? '#C9A227' : (mode === 'dark' ? '#E8DCC4' : '#0F4C3A'))
                                  : undefined 
                            }}
                          />}
                        </div>
                        <span className={`font-semibold text-base flex-1 ${
                          isEmeraldTheme 
                            ? (isActive ? 'text-black' : 'text-white')
                            : ''
                        }`}>
                          {item.label}
                        </span>
                        {isItemLocked && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center gap-0.5">
                            <Crown className="h-2.5 w-2.5" />
                            PRO
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </nav>
                
                {/* Footer */}
                <div className={`p-4 border-t ${appTheme === 'classic' ? 'border-[#C9A227]/30' : 'border-border/30'}`}>
                  <div className={`rounded-2xl p-4 text-center ${appTheme === 'classic' ? 'bg-[#C9A227]/10' : 'bg-muted/50'}`}>
                    <p className={`text-xs ${appTheme === 'classic' ? 'text-[#0F4C3A]' : 'text-muted-foreground'}`}>
                      Tanzeen <span className="font-black" style={{fontWeight: 900}}>+</span> | {t('app.tagline')}
                    </p>
                  </div>
                </div>
              </div>
            </SheetContent>
            )}
          </Sheet>
        )}
        </div>
      </div>
      
      {/* Classic Theme - Show Prayer Row instead of Row 2, Title, Progress */}
      {appTheme === 'classic' ? (
        <ClassicPrayerRow />
      ) : (
        <>
      {/* Row 2: Quran Book + Juz Button | Globe + Palette - SLIGHTLY LIGHTER */}
      <div 
        className="flex items-center justify-between px-4 py-2"
        style={{ backgroundColor: colors.row2Bg }}
      >
        <div className="flex items-center gap-3">
          {/* Quran Book Image */}
          <button
            onClick={openMushafMode}
            className="flex-shrink-0 group cursor-pointer"
            title={t('quran.mushafMode', 'Open Quran Mode')}
            data-testid="mushaf-mode-btn"
          >
            <img 
              src="/images/quran-book.png" 
              alt="Quran" 
              className="h-11 w-11 object-contain group-hover:scale-105 transition-transform" 
            />
          </button>
          
          {/* Juz Button */}
          {showJuzButton && (
            <Dialog open={juzDialogOpen} onOpenChange={setJuzDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  size="sm" 
                  className={`rounded-lg ${colors.juzBtnBg} ${colors.juzBtnText} hover:opacity-90 h-9 px-3`} 
                  data-testid="juz-btn"
                >
                  <Layers className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              {renderJuzDialog()}
            </Dialog>
          )}
        </div>
        
        {/* Globe + Palette Icons */}
        <div className="flex items-center gap-3">
          <Dialog open={languageDialogOpen} onOpenChange={setLanguageDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="icon" 
                className={`rounded-full border ${colors.iconBtnBorder} ${colors.iconBtnText} ${colors.iconBtnHover} h-10 w-10 bg-transparent`} 
                data-testid="language-btn"
              >
                <Globe className="h-5 w-5" />
              </Button>
            </DialogTrigger>
            {renderLanguageDialog()}
          </Dialog>
          
          <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="icon" 
                className={`rounded-full border ${colors.iconBtnBorder} ${colors.iconBtnText} ${colors.iconBtnHover} h-10 w-10 bg-transparent`} 
                data-testid="theme-btn"
              >
                <Palette className="h-5 w-5" />
              </Button>
            </DialogTrigger>
            {renderThemeDialog()}
          </Dialog>
        </div>
      </div>
      
      {/* Page Title Section (between header and progress bar) - CENTERED */}
      {pageTitle && (
        <div 
          className="px-4 py-3"
          style={{ backgroundColor: colors.titleBg }}
        >
          <h1 className={`text-xl font-bold text-center ${colors.titleText}`}>
            {pageTitle}
          </h1>
        </div>
      )}
      
      {/* Progress Bar - with spacing from header (conditionally shown) */}
      {showProgressBar && (
        <div 
          className={`mx-4 mt-3 mb-3 flex items-center gap-3 ${colors.progressBg} rounded-xl px-4 py-2 cursor-pointer`}
          onClick={() => navigate('/progress')}
          data-testid="header-progress-bar"
        >
          <div className="flex-1">
            <Progress 
              value={completionPercentage} 
              className="h-2"
              indicatorClassName={colors.progressIndicator}
            />
          </div>
          <span className={`${colors.progressText} text-sm font-medium`}>
            {completionPercentage}%
          </span>
          <ChevronDown className={`h-4 w-4 ${colors.progressText}`} />
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default PageHeader;
